import { useState } from "react";
import { GraduationCap, Edit3, Trash2, Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { useCurrentUser } from "@/pages/auth/hooks/useAuth";
import { useDeleteEducation } from "./hooks/useEducation";
import { useCandidateEducation } from "@/pages/candidate-profile/hooks/useCandidateProfile";
import { EducationFormModal } from "./components/EducationFormModal";
import type { EducationDetail } from "@/interface/profile";

export default function EducationTab({
  profileId,
}: {
  profileId?: number | string;
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEdu, setSelectedEdu] = useState<EducationDetail | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const { data: user } = useCurrentUser();
  const targetId = profileId || user?.id;

  const {
    data: candEduRes,
    isLoading: isCandLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCandidateEducation(targetId, { page: 1, page_size: 10 });
  const deleteMutation = useDeleteEducation();

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }
  };

  const educationList =
    candEduRes?.pages.flatMap((page: any) =>
      Array.isArray(page?.data) ? page.data : page?.data?.results || [],
    ) || [];

  const isLoading = isCandLoading && educationList?.length === 0;

  const handleOpenAdd = () => {
    setSelectedEdu(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (edu: EducationDetail) => {
    setSelectedEdu(edu);
    setIsModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;
    try {
      await deleteMutation.mutateAsync(deleteTargetId);
      setDeleteTargetId(null);
    } catch {
      // Error handled by mutation
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <GraduationCap className="w-5 h-5 text-purple-600 dark:text-purple-400" /> Education
        </h2>
        <Button
          onClick={handleOpenAdd}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-bold rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Plus className="w-4 h-4" /> Add Education
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center p-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      ) : (
        <div
          onScroll={handleScroll}
          className="space-y-6 max-h-[700px] overflow-y-auto pr-2 custom-scrollbar"
        >
          {educationList?.map((edu: EducationDetail) => {
            const degreeName = edu.degree?.degree_name || "Education Record";
            const institution = edu.institution || "";
            const location = edu.location || "";
            const grade = edu.grade_cgpa;

            const startYear = edu.start_year || "";
            const endYear = edu.end_year || "Present";
            const dateDisplay =
              startYear || endYear
                ? `${startYear}${startYear && endYear ? " - " : ""}${endYear}`
                : "";

            return (
              <div
                key={edu.id}
                className="flex gap-4 p-5 rounded-xl border border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 hover:border-purple-200 dark:hover:border-purple-800 transition-colors group relative"
              >
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                  <button
                    onClick={() => handleOpenEdit(edu)}
                    className="p-1.5 text-slate-400 hover:text-purple-600 dark:hover:text-purple-400 bg-white dark:bg-slate-800 hover:bg-purple-50 dark:hover:bg-purple-950/40 rounded transition-colors shadow-sm"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => setDeleteTargetId(String(edu.id))}
                    className="p-1.5 text-slate-400 hover:text-red-600 dark:hover:text-red-400 bg-white dark:bg-slate-800 hover:bg-red-50 dark:hover:bg-red-950/40 rounded transition-colors shadow-sm"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>

                <div className="w-12 h-12 bg-white dark:bg-slate-800 rounded-full flex items-center justify-center shadow-sm shrink-0">
                  <GraduationCap className="w-6 h-6 text-slate-400 dark:text-slate-300" />
                </div>

                <div className="flex-1">
                  <h3 className="font-bold text-slate-900 dark:text-white text-lg mb-1">
                    {degreeName}
                  </h3>
                  <div className="text-slate-600 dark:text-slate-300 font-medium text-sm mb-2">
                    {institution}
                    {institution && location ? " • " : ""}
                    {location}
                  </div>

                  <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 dark:text-slate-400 mb-3">
                    {dateDisplay && (
                      <span className="flex items-center gap-1 font-semibold text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 px-2 py-1 rounded-md border border-slate-200 dark:border-slate-700">
                        {dateDisplay}
                      </span>
                    )}
                    {grade && (
                      <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-100 dark:border-emerald-900/40 font-bold px-2 py-1 rounded-md">
                        Grade: {grade}
                      </span>
                    )}
                  </div>

                  {edu.description && (
                    <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed max-w-3xl whitespace-pre-line">
                      {edu.description}
                    </p>
                  )}
                </div>
              </div>
            );
          })}

          {educationList.length === 0 && (
            <div className="text-center p-12 text-slate-500 dark:text-slate-400 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-xl bg-white dark:bg-slate-900">
              No education history added yet.
            </div>
          )}

          {isFetchingNextPage && (
            <div className="flex justify-center items-center py-6">
              <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
              <span className="ml-2 text-sm text-slate-500 dark:text-slate-400 font-medium">
                Loading more education...
              </span>
            </div>
          )}
        </div>
      )}

      <EducationFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        editData={selectedEdu}
        profileId={targetId}
      />

      <ConfirmationDialog
        isOpen={Boolean(deleteTargetId)}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Education"
        description="Are you sure you want to remove this education record?"
        confirmText="Delete"
        confirmVariant="danger"
        isPending={deleteMutation.isPending}
      />
    </div>
  );
}
